import { colorBrandForeground2, colorNeutralBackground4, colorNeutralForeground1, colorNeutralForegroundDisabled, colorNeutralForegroundOnBrand, colorNeutralStrokeDisabled, colorPaletteRedForeground3, display, fontFamilyBase, fontSizeBase200, fontSizeBase300, fontWeightRegular, fontWeightSemibold, lineHeightBase200, lineHeightBase300, spacingHorizontalS, spacingHorizontalM, spacingHorizontalXXL, spacingVerticalXXS, spacingVerticalL, spacingVerticalM, strokeWidthThin, strokeWidthThick } from "@fluentui/web-components";
import { sizeHorizontalM, sizeHorizontalXL, sizeHorizontalXXL, sizeVerticalM, sizeVerticalXL, sizeVerticalXXL } from "@fabric-msft/theme";
import { css } from "@microsoft/fast-element";
/** Wizard Step styles
 * @public
 */
export const styles = css `
  ${display("block")}

  .step {
    unset: all;
    background: transparent;
    color: ${colorNeutralForeground1};
    text-align: start;
    border: 0 none;
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    font-family: ${fontFamilyBase};
    position: relative;
    align-items: flex-start;
    column-gap: ${spacingHorizontalM};
    flex-shrink: 0;
    flex-grow: 1;
  }

  :host([hide-connector]) .step-connector,
  .summary,
  .title,
  .details {
    display: none;
  }

  :host([disabled]) .step {
    cursor: not-allowed;
    pointer-events: none;
    color: ${colorNeutralForegroundDisabled};
  }

  :host([disabled]) .step-connector {
    background: ${colorNeutralStrokeDisabled};
  }

  :host([disabled]) .icon {
    background: ${colorNeutralBackground4};
    border-color: ${colorNeutralStrokeDisabled};
  }

  :host(.overflow) {
    flex-shrink: 0;
    flex-grow: 0;
    width: 36px; /* @design-token-ok: component-specific step indicator container width */
  }

  :host(.first) {
    padding-inline-start: ${spacingHorizontalXXL};
  }

  :host(.first) .step-connector {
    inset-inline-start: 28px; /* @design-token-ok: connector offset aligns with step indicator center */
  }

  :host(.last) {
    padding-inline-end: ${spacingHorizontalXXL};
  }

  .state-indicator {
    width: ${sizeHorizontalXXL};
    height: ${sizeVerticalXXL};
    display: flex;
    justify-content: center;
    align-items: center;
    padding: ${spacingVerticalXXS};
    box-sizing: border-box;
  }

  .icon {
    position: relative;
    z-index: 9;
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${sizeHorizontalXL};
    height: ${sizeVerticalXL};
    font-weight: var(--fontWeightRegular);
    line-height: var(--lineHeightBase100);
    font-size: var(--fontSizeBase200);
    border-radius: var(--borderRadiusCircular);
    border: ${strokeWidthThick} solid var(--colorNeutralForeground2);
    background: var(--colorNeutralBackground4);
    flex-shrink: 0;
    box-sizing: border-box;
  }

  .order {
    font-size: var(--fontSizeBase200);
    font-weight: var(--fontWeightSemibold);
    line-height: var(--lineHeightBase200);
    margin-bottom: 1px; /* @design-token-ok: single-pixel optical alignment correction */
  }

  .step-connector {
    position: absolute;
    top: ${spacingVerticalM};
    inset-inline-start: ${spacingHorizontalS};
    height: ${strokeWidthThick};
    width: 100%;
    background: var(--colorNeutralForegroundDisabled);
  }

  .title {
    font-weight: ${fontWeightRegular};
    font-size: ${fontSizeBase300};
    line-height: ${lineHeightBase300};
  }

  .details {
    font-weight: ${fontWeightRegular};
    font-size: ${fontSizeBase200};
    line-height: ${lineHeightBase200};
  }

  .icon svg {
    color: ${colorNeutralForegroundOnBrand};
    fill: ${colorNeutralForegroundOnBrand};
    width: ${sizeHorizontalM};
    height: ${sizeVerticalM};
    box-sizing: border-box;
    vertical-align: middle;
  }

  :host([hide-connector]) .step {
    width: fit-content;
    flex-grow: 0;
    min-width: unset;
  }

  :host([aria-current="step"]) .title {
    font-weight: ${fontWeightSemibold};
  }

  :host([state="complete"]) .icon,
  :host([state="complete"]) .step-connector,
  :host([state="complete"].first) .step-connector,
  :host([active]) .icon {
    background: ${colorBrandForeground2};
    border-color: ${colorBrandForeground2};
    color: ${colorNeutralForegroundOnBrand};
  }

  :host([state="complete"]) .icon svg {
    margin-top: ${spacingVerticalXXS};
  }

  :host([state="error"]) .icon {
    background: ${colorPaletteRedForeground3};
    border-color: ${colorPaletteRedForeground3};
  }

  @media (min-width: 32.5rem /* @design-token-ok: aligns with wizard layout breakpoint */) {
    :host(.overflow) .step,
    :host .step,
    :host(.first) .step {
      display: flex;
      align-items: flex-start;
      width: fit-content;
      height: fit-content;
      max-width: 268px; /* @design-token-ok: component-specific step max-width */
      padding: 0 0 ${spacingVerticalL} 0;
      column-gap: ${spacingHorizontalM};
      flex-grow: 0;
    }
    :host(.first) .step-connector,
    .step-connector {
      position: absolute;
      width: ${strokeWidthThick};
      inset-inline-start: 11px; /* @design-token-ok: connector aligns with step indicator center */
      height: 100%;
      background: var(--colorNeutralForegroundDisabled);
      min-height: 22px; /* @design-token-ok: minimum connector length */
    }
  }

  @media (min-width: calc(${spacingHorizontalXXL} * 43 - ${spacingHorizontalS} - ${strokeWidthThin})) {
    :host .step,
    :host(.first) .step {
      flex-direction: row;
    }
    .step-connector {
      inset-inline-start: 11px; /* @design-token-ok: connector aligns with step indicator center */
    }
    :host([aria-current="step"]) .details,
    .title {
      display: block;
    }
    .order {
      display: none;
    }
    .summary {
      display: flex;
      flex-direction: column;
      width: fit-content;
      gap: ${spacingVerticalXXS};
    }
  }
  :host(:hover) .step {
    cursor: pointer;
  }
  :host([disabled]) .step {
    cursor: not-allowed;
  }
`;
//# sourceMappingURL=wizard-step.styles.js.map